import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  EntitaMemoria,
  HandlerInfo,
  MemoriaRegola,
  RegolaCreaInput,
  RegoleScope,
  ScopeTipo,
  TipoRegola,
} from "@/types/memoria";

export function useMemoriaEntita(scope: ScopeTipo) {
  return useQuery({
    queryKey: ["memoria", "entita", scope],
    queryFn: () => apiFetch<EntitaMemoria[]>(`/api/v1/memoria/entita/${scope}`),
  });
}

export function useMemoriaRegole(scope: ScopeTipo | null, ref: string | null) {
  return useQuery({
    queryKey: ["memoria", "regole", scope, ref],
    queryFn: () => apiFetch<RegoleScope>(`/api/v1/memoria/${scope}/${ref}/regole`),
    enabled: scope !== null && ref !== null,
  });
}

export function useTipiRegola() {
  return useQuery({
    queryKey: ["memoria", "tipi"],
    queryFn: () => apiFetch<TipoRegola[]>("/api/v1/memoria/tipi"),
  });
}

export function useHandlersMemoria() {
  return useQuery({
    queryKey: ["memoria", "handlers"],
    queryFn: () => apiFetch<HandlerInfo[]>("/api/v1/memoria/handlers"),
  });
}

function useInvalidaRegole() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["memoria", "regole"] });
    qc.invalidateQueries({ queryKey: ["memoria", "entita"] });
  };
}

export function useCreaRegola() {
  const invalida = useInvalidaRegole();
  return useMutation({
    mutationFn: (input: RegolaCreaInput) =>
      apiFetch<MemoriaRegola>("/api/v1/memoria/regole", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: invalida,
  });
}

export function useAzioneRegola(azione: "conferma" | "sospendi" | "riattiva" | "revoca") {
  const invalida = useInvalidaRegole();
  return useMutation({
    mutationFn: (regolaId: string) =>
      apiFetch<MemoriaRegola>(`/api/v1/memoria/regole/${regolaId}/${azione}`, { method: "POST" }),
    onSuccess: invalida,
  });
}

export interface ChatTurnoInput {
  scope_tipo: ScopeTipo;
  scope_ref: string;
  conversazione_id: string | null;
  messaggio: string;
}

export interface ChatTurnoRisposta {
  conversazione_id: string;
  risposta: string;
  regola_id: string | null;
}

export function useMemoriaChat() {
  const invalida = useInvalidaRegole();
  return useMutation({
    mutationFn: (input: ChatTurnoInput) =>
      apiFetch<ChatTurnoRisposta>("/api/v1/memoria/chat", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (res) => {
      if (res.regola_id) invalida();
    },
  });
}

export interface TipoCreaRisposta {
  creato: TipoRegola | null;
  simili: TipoRegola[];
}

export function useCreaTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { etichetta: string; descrizione?: string; forza?: boolean }) =>
      apiFetch<TipoCreaRisposta>("/api/v1/memoria/tipi", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memoria", "tipi"] }),
  });
}

export function useCollegaHandler() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tipoId, handler }: { tipoId: string; handler: string | null }) =>
      apiFetch<TipoRegola>(`/api/v1/memoria/tipi/${tipoId}/handler`, {
        method: "POST",
        body: JSON.stringify({ handler_codice: handler }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memoria", "tipi"] });
      qc.invalidateQueries({ queryKey: ["memoria", "regole"] });
    },
  });
}

export function useModificaTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tipoId, etichetta, descrizione }: { tipoId: string; etichetta?: string; descrizione?: string }) =>
      apiFetch<TipoRegola>(`/api/v1/memoria/tipi/${tipoId}`, {
        method: "PUT",
        body: JSON.stringify({ etichetta, descrizione }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memoria", "tipi"] }),
  });
}

export function useEliminaTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tipoId: string) =>
      apiFetch<{ soft: boolean; regole: number }>(`/api/v1/memoria/tipi/${tipoId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memoria", "tipi"] });
      qc.invalidateQueries({ queryKey: ["memoria", "regole"] });
    },
  });
}

export function useMergeTipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ daId, aId }: { daId: string; aId: string }) =>
      apiFetch<void>("/api/v1/memoria/tipi/merge", {
        method: "POST",
        body: JSON.stringify({ da_id: daId, a_id: aId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memoria", "tipi"] }),
  });
}

export function useModificaRegola() {
  const invalida = useInvalidaRegole();
  return useMutation({
    mutationFn: ({ id, testo }: { id: string; testo: string }) =>
      apiFetch<MemoriaRegola>(`/api/v1/memoria/regole/${id}`, {
        method: "PUT",
        body: JSON.stringify({ testo }),
      }),
    onSuccess: invalida,
  });
}
